import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import { COPENHAGEN_TIME_NOTE } from '../../common/copenhagenTime';
import { JobStatus, STATUS_HINT, cph, duration } from './DeviceJobsShared';

/**
 * Device job — one booking or cancellation run on a device.
 *
 * Read-only, like the list. Laid out for the question it is opened with, "what
 * happened on the phone?": the status and its meaning first (an Unknown job gets
 * the warning, not a neutral pill), then the timings, then the steps the script
 * went through, then the raw request, result and logs.
 *
 * The flow is the script's own trail: `{ step, message, timestamp }` per step.
 * Anything else in it is shown as JSON rather than guessed at.
 */

const ACTIVE = ['Queued', 'Claimed'];

const pretty = value => (typeof value === 'string' ? value : JSON.stringify(value, null, 2));

const FlowTable = ({ flow }) => {
  const start = flow.find(x => x && x.timestamp)?.timestamp;

  return (
    <div className="next-table-wrap">
      <table className="next-table next-table--stack">
        <thead>
          <tr>
            <th>#</th>
            <th>Step</th>
            <th>Message</th>
            <th className="next-table__right">At</th>
          </tr>
        </thead>
        <tbody>
          {flow.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index}>
              <td data-label="#" className="next-muted">{index + 1}</td>
              {item && typeof item === 'object' && item.step ? (
                <>
                  <td data-label="Step"><strong>{item.step}</strong></td>
                  <td data-label="Message" className="next-break">{item.message || <span className="next-muted">—</span>}</td>
                  <td data-label="At" className="next-table__right next-nowrap">
                    {item.timestamp ? `+${duration(start, item.timestamp) || '0 s'}` : '—'}
                  </td>
                </>
              ) : (
                <td colSpan={3} data-label="Step"><pre className="next-pre">{pretty(item)}</pre></td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

FlowTable.propTypes = {
  flow: PropTypes.array.isRequired
};

const DeviceJobInternal = ({ api }) => {
  const { jobId } = useParams();

  const [job, setJob] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.deviceJobs.getDeviceJob(jobId);
      if (!data || !data.id) {
        setFailed(true);
        return;
      }
      setJob(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, jobId]);

  useEffect(() => {
    load();
  }, [load]);

  // A job still in the queue or on the device changes by itself: follow it.
  useEffect(() => {
    if (!job || !ACTIVE.includes(job.status)) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 5000);
    return () => clearInterval(timer);
  }, [job, load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/device-jobs">← All device jobs</BackToList>
        <div className="next-note next-note--error">
          Could not load this device job.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!job) return <div className="next-page next-muted">Loading…</div>;

  const result = job.result && typeof job.result === 'object' ? job.result : null;
  const flow = Array.isArray(result?.flow) ? result.flow : null;
  const muted = text => <span className="next-muted">{text}</span>;

  const facts = [
    ['Status', <JobStatus status={job.status} />],
    ['Action', job.action],
    ['Script', job.script],
    ['Venue', job.facilityName || job.productName || muted('—')],
    ['Product', job.productName || muted('—')],
    ['System', job.systemType || muted('—')],
    ['Integration id', <Guid value={job.integrationId} />],
    ['Signup', job.signupId ? <Guid value={job.signupId} to={`/next/signup/${job.signupId}`} /> : muted('—')],
    ['User', job.userEmail || muted('—')],
    ['Activity starts', job.activityStartsAt ? cph(job.activityStartsAt) : muted('—')],
    ['Created', cph(job.createdAt)],
    ['Claimed', job.claimedAt ? `${cph(job.claimedAt)} by ${job.claimedBy || '?'}` : muted('not claimed')],
    ['Finished', job.finishedAt ? cph(job.finishedAt) : muted('—')],
    ['Waited for a runner', duration(job.createdAt, job.claimedAt) || muted('—')],
    ['Ran for', duration(job.claimedAt, job.finishedAt) || muted('—')],
    ['Script time', result?.seconds != null ? `${result.seconds} s` : muted('—')],
    ['Reference', job.reference || muted('—')],
    ['Pincode', job.pincode || muted('—')],
    ['Money spent', result?.moneySpent != null && result.moneySpent !== '' ? String(result.moneySpent) : muted('—')],
    ['Card used', result?.cardUsed || muted('—')],
    ['Dry run', result ? (result.dryRun ? 'Yes' : 'No') : muted('—')]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/device-jobs">← All device jobs</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{job.action} on device</h1>
          <JobStatus status={job.status} />
        </div>
        <span className="next-detail__id">{job.id}</span>
      </header>

      {job.status === 'Unknown' && (
        <div className="next-note next-note--warn">
          <strong>Unknown.</strong> {STATUS_HINT.Unknown}
        </div>
      )}
      {ACTIVE.includes(job.status) && (
        <div className="next-note next-note--info">
          {STATUS_HINT[job.status]} This page refreshes by itself every 5 seconds.
        </div>
      )}
      {job.error && (
        <div className="next-note next-note--error">
          <strong>Error:</strong> {job.error}
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Job</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          {(result?.screenshotUrl || result?.screenshot || result?.pageSourceUrl) && (
            <section className="next-card">
              <div className="next-card__head"><h2>Phone screen at the failure</h2></div>
              <p className="next-table__note next-muted">
                {result.screenshotUrl && <a href={result.screenshotUrl} target="_blank" rel="noopener noreferrer">Screenshot</a>}
                {result.screenshotUrl && result.pageSourceUrl && ' · '}
                {result.pageSourceUrl && <a href={result.pageSourceUrl} target="_blank" rel="noopener noreferrer">Page source (XML)</a>}
                {result.pageSourceUrl && ': every element on the screen, with label, position and visibility.'}
              </p>
              {result.screenshotUrl
                ? (
                  <a href={result.screenshotUrl} target="_blank" rel="noopener noreferrer">
                    <img src={result.screenshotUrl} alt="Phone screen when the script failed" style={{ maxWidth: '320px', width: '100%', border: '1px solid #ddd', borderRadius: '8px' }} />
                  </a>
                )
                : result.screenshot && <p className="next-table__note next-muted">Screenshot not uploaded: only on the runner Mac, at <code>{result.screenshot}</code> (deleted by macOS after a few days).</p>}
            </section>
          )}

          <section className="next-card">
            <div className="next-card__head"><h2>Flow</h2></div>
            {flow && flow.length > 0
              ? <FlowTable flow={flow} />
              : <p className="next-table__note next-muted">No steps reported{job.finishedAt ? '' : ' yet'}.</p>}
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Logs</h2></div>
            {job.logs
              ? <pre className="next-pre">{job.logs}</pre>
              : <p className="next-table__note next-muted">No logs reported{job.finishedAt ? '' : ' yet'}.</p>}
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Result</h2></div>
            {job.result != null
              ? <pre className="next-pre">{pretty(job.result)}</pre>
              : <p className="next-table__note next-muted">No result reported{job.finishedAt ? '' : ' yet'}.</p>}
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Request</h2></div>
            <p className="next-table__note next-muted">What the script received, without the account credentials (added by the backend at claim time, never stored).</p>
            {job.request != null
              ? <pre className="next-pre">{pretty(job.request)}</pre>
              : <p className="next-table__note next-muted">No request stored.</p>}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                job.signupId && { label: 'Signup', to: `/next/signup/${job.signupId}` },
                job.signupId && {
                  label: 'Other device jobs',
                  hint: 'of this signup',
                  to: `/next/device-jobs?signupId=${job.signupId}`
                },
                {
                  label: 'Device jobs',
                  hint: 'of this integration',
                  to: `/next/device-jobs?integrationId=${job.integrationId}`
                },
                job.productId && {
                  label: 'Integration',
                  href: `/product/${job.productId}/integration`
                }
              ]}
            />
          </section>
          <p className="next-muted next-page__footnote">{COPENHAGEN_TIME_NOTE}</p>
        </aside>
      </div>
    </div>
  );
};

DeviceJobInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const DeviceJob = withApi(DeviceJobInternal);

DeviceJob.requiresLogin = true;

export default DeviceJob;
